import { useEffect, useId, useState } from "react";

import { Button, Dialog, Field, Input, Notice, Textarea, plainError } from "../../../components/ui";
import { fullDate, isIsoDate } from "../../../components/calendar";
import { businessApi } from "../../../services/api";
import { CLOSURE_LIMITS, closureConsequence, closureFormErrors } from "./calendarView";

/**
 * Add or change a closure day: a date, a name and an optional note — nothing else. A closure is a day the business
 * does not operate; it carries no holiday class, pay rate or recurrence, so the form offers none. The text under the
 * date says what closing that date means before it is saved (the filled form is the review; the server decides).
 *
 * `officialOn(date)` resolves the official observances of a date (the page caches the years it has loaded).
 */
export default function ClosureDayDialog({ closure = null, today, lastDate, officialOn, onClose, onSaved }) {
  const formId = useId();
  const editing = Boolean(closure);
  const [form, setForm] = useState({ date: closure?.date || "", name: closure?.name || "", note: closure?.note || "" });
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState("");
  const [stale, setStale] = useState(false);
  const [saving, setSaving] = useState(false);
  const [official, setOfficial] = useState({ date: "", days: null });

  // What is official on the chosen date, for the consequence text. Unknown while it loads or if it cannot be read.
  useEffect(() => {
    if (!isIsoDate(form.date)) return undefined;
    let active = true;
    Promise.resolve(officialOn(form.date))
      .then((days) => { if (active) setOfficial({ date: form.date, days: Array.isArray(days) ? days : null }); })
      .catch(() => { if (active) setOfficial({ date: form.date, days: null }); });
    return () => { active = false; };
  }, [form.date, officialOn]);

  const set = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    setFailure("");
  };

  const known = official.date === form.date && official.days ? official.days : null;
  const consequence = isIsoDate(form.date) && known ? closureConsequence(known) : null;

  const submit = async (event) => {
    event.preventDefault();
    if (saving || stale) return;
    const found = closureFormErrors(form, { today, lastDate });
    setErrors(found);
    setFailure("");
    const first = ["date", "name", "note"].find((key) => found[key]);
    if (first) {
      document.getElementById(`${formId}-${first}`)?.focus();
      return;
    }
    const payload = { date: form.date, name: form.name.trim(), note: form.note.trim() || null };
    setSaving(true);
    try {
      const saved = editing
        ? await businessApi.updateClosureDay(closure.id, { ...payload, expectedUpdatedAt: closure.updatedAt || undefined })
        : await businessApi.createClosureDay(payload);
      // The page closes this dialog; nothing more is set here.
      onSaved(saved?.closure || payload, editing);
      return;
    } catch (err) {
      const message = plainError(err, "The closure day was not saved. Try again.");
      const field = err?.serverDetails?.field;
      if (err?.serverCode === "CLOSURE_CHANGED" || (editing && err?.status === 404)) {
        setStale(true);
        setFailure(err?.status === 404 ? "This closure day no longer exists. Close this and refresh the calendar." : message);
      } else if (["date", "name", "note"].includes(field)) {
        setErrors({ [field]: message });
        document.getElementById(`${formId}-${field}`)?.focus();
      } else {
        setFailure(err?.status === 403 ? "You can't change closure days. Ask someone who manages the calendar." : message);
      }
    }
    setSaving(false);
  };

  const noteLength = form.note.trim().length;
  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open && !saving) onClose(stale); }}
      title={editing ? "Change closure day" : "Add closure day"}
      description="A day your business does not operate."
      footer={(
        <>
          <Button onClick={() => onClose(stale)} disabled={saving}>{stale ? "Close" : "Cancel"}</Button>
          {stale ? null : (
            <Button type="submit" form={formId} variant="primary" loading={saving} disabled={Boolean(consequence?.blocked)}>
              {editing ? "Save changes" : "Add closure day"}
            </Button>
          )}
        </>
      )}
    >
      <form id={formId} onSubmit={submit} noValidate className="space-y-4">
        <Field label="Date" required id={`${formId}-date`} error={errors.date}
          hint={isIsoDate(form.date) ? fullDate(form.date) : "Today or a later date."}>
          <Input type="date" value={form.date} min={today || undefined} max={lastDate || undefined} onChange={set("date")} disabled={saving || stale} />
        </Field>
        {consequence ? (
          <Notice tone={consequence.tone} role={consequence.blocked ? "alert" : undefined}>{consequence.text}</Notice>
        ) : null}
        <Field label="Name" required id={`${formId}-name`} error={errors.name} hint="Shown to employees, for example Stock count.">
          <Input value={form.name} maxLength={CLOSURE_LIMITS.nameMax} autoComplete="off" onChange={set("name")} disabled={saving || stale} />
        </Field>
        <Field label="Note" id={`${formId}-note`} error={errors.note} hint={`Optional. ${noteLength} of ${CLOSURE_LIMITS.noteMax} characters.`}>
          <Textarea value={form.note} maxLength={CLOSURE_LIMITS.noteMax} rows={3} onChange={set("note")} disabled={saving || stale} />
        </Field>
        {failure ? <Notice tone="danger" title={stale ? "Out of date" : "Not saved"} role="alert">{failure}</Notice> : null}
      </form>
    </Dialog>
  );
}
